import * as React from 'react';

import {
  formatStandardDate,
  isoCalendarDay,
  type FormatStandardDateOptions,
} from './format-standard-date';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

export interface DateTooltipProps {
  /** The underlying value being displayed — the tooltip shows ITS iso day. */
  value: Date | string | null | undefined;
  children: React.ReactNode;
  className?: string | undefined;
}

/**
 * Wraps `children` in a hover tooltip showing `value`'s plain ISO calendar
 * day (`yyyy-MM-dd`) — the unambiguous raw date, for anyone who needs it
 * instead of hunting through the standard "Mon 28th Jul" format. Renders
 * `children` plain, with no tooltip, when `value` doesn't parse to a date.
 */
function DateTooltip({ value, children, className }: DateTooltipProps) {
  const iso = isoCalendarDay(value);
  if (!iso) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={className}>{children}</span>
      </TooltipTrigger>
      <TooltipContent>{iso}</TooltipContent>
    </Tooltip>
  );
}

export interface StandardDateProps extends FormatStandardDateOptions {
  value: Date | string | null | undefined;
  className?: string;
}

/**
 * Drop-in replacement for `{formatStandardDate(value)}` as JSX: renders the
 * standard format and wraps it in a `DateTooltip` showing the raw ISO day.
 * Renders nothing for null/invalid input.
 */
function StandardDate({ value, now, className }: StandardDateProps) {
  const formatted = formatStandardDate(value, { now });
  if (!formatted) return null;
  return (
    <DateTooltip value={value} className={className}>
      {formatted}
    </DateTooltip>
  );
}

export { DateTooltip, StandardDate };
